import React, { useEffect, useState } from 'react';
import { Form, FormInstance, TextArea } from '@lightbeamhealth/blueprint-form';
import { faPlusCircle, faStickyNote } from '@fortawesome/pro-solid-svg-icons';

import { AppKey } from '../../../libs/apps';
import { IEvent } from '../../../libs/event';
import { useRequestOnEvent } from '../../../libs/hooks/useRequestJSON';
import { BaseResponse, PostJSON } from '../../../libs/request';

import Icon from '../../atoms/icon';
import Button from '../../molecules/button';
import BareCollapse from '../collapse/bareCollapse';
import ErrorCallout from '../callout/error-callout';
import Toast from '../toast';

interface CreateEventData {
  note: string;
}
interface CreateEventParams {
  app: AppKey;
  note: string;
  patientId: number;
}
interface CreateEventResponse extends BaseResponse {
  event: IEvent;
}

interface EventCreateProps {
  appKey?: AppKey;
  patientId: number;
  handleNewEvent: (newEvent: IEvent) => void;
}
const EventCreate: React.FC<EventCreateProps> = ({
  appKey,
  patientId,
  handleNewEvent
}) => {
  const { data, isLoading, error, run, resetRequest } = useRequestOnEvent<
    CreateEventParams,
    CreateEventResponse
  >();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [hasNote, setHasNote] = useState<boolean>(false);
  const form = new FormInstance({ note: '' });

  const handleChange = (data: CreateEventData) => {
    setHasNote(data.note.trim().length > 0);
  };
  const handleClose = () => {
    setIsOpen(false);
  };
  const handleOpen = () => {
    setIsOpen(true);
  };
  const handleSubmit = (data: CreateEventData) => {
    const params: CreateEventParams = {
      app: appKey,
      patientId,
      note: data.note
    };
    const request = () =>
      PostJSON<CreateEventResponse, CreateEventParams>({
        url: '/event/create/',
        params
      });
    run(request);
  };

  useEffect(() => {
    if (data && data.status === 'OK') {
      Toast({
        intent: 'success',
        message: 'Your note has been added!',
        timeout: 2000
      });
      handleNewEvent(data.event);
      handleClose();
      resetRequest();
    }
  }, [data]);

  return (
    <>
      <Button
        className="mb-4"
        icon={<Icon className="mr-3" icon={faStickyNote} />}
        onClick={handleOpen}
      >
        Add New Event Note
      </Button>
      <BareCollapse className="mb-4" isOpen={isOpen}>
        <Form
          form={form}
          onChange={handleChange}
          onSubmit={handleSubmit}
          validateOnChange
        >
          {error && <ErrorCallout className="mb-2">{error}</ErrorCallout>}
          <TextArea className="mt-2" fill name="note" required spellCheck />
          <Button
            className="text-white mr-4"
            disabled={!hasNote}
            icon={<Icon className="mr-3" icon={faPlusCircle} />}
            intent="success"
            loading={isLoading}
            type="submit"
          >
            Save Note
          </Button>
          <Button variant="minimal" onClick={handleClose}>
            Cancel
          </Button>
        </Form>
      </BareCollapse>
    </>
  );
};

export default EventCreate;
